import { useEffect, useRef, useState } from 'react';
import { paths } from '../../app/router';
import { IconDots, IconPin } from '../../ui/icons';
import { useBoard, usePane, usePaneStore, useYMap } from '../context';
import { centerOn, screenToWorld } from '../viewport/camera';

export interface SavedView {
  name: string;
  cx: number;
  cy: number;
  zoom: number;
  order: number;
}

/**
 * Quick-link buttons for saved viewports. A view stores the world point at the centre of the
 * pane plus the zoom, so it means the same thing on any screen size. Views live in the board
 * document and sync like items. `#/b/<id>?view=<viewId>` opens a board at a view.
 */
export function ViewBar() {
  const board = useBoard();
  const pane = usePaneStore();
  const views = useYMap<SavedView>(board.views);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuFor) return;
    const close = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuFor(null);
    };
    window.addEventListener('pointerdown', close, true);
    return () => window.removeEventListener('pointerdown', close, true);
  }, [menuFor]);

  const entries = Object.entries(views).sort((a, b) => a[1].order - b[1].order);

  const currentView = (): Omit<SavedView, 'name' | 'order'> => {
    const { camera, size } = pane.getState();
    const c = screenToWorld(camera, { x: size.w / 2, y: size.h / 2 });
    return { cx: round(c.x), cy: round(c.y), zoom: round(camera.zoom) };
  };

  const save = () => {
    const name = prompt('View name', `View ${entries.length + 1}`);
    if (!name?.trim()) return;
    const order = entries.length ? Math.max(...entries.map(([, v]) => v.order)) + 1 : 0;
    board.saveView({ name: name.trim(), order, ...currentView() });
  };

  const go = (v: SavedView) => {
    const { size, setCamera } = pane.getState();
    setCamera(centerOn({ x: v.cx, y: v.cy }, size, v.zoom));
  };

  const act = (fn: () => void) => () => {
    setMenuFor(null);
    fn();
  };

  return (
    <div className="viewbar" onPointerDown={(e) => e.stopPropagation()}>
      {entries.map(([id, v]) => (
        <span key={id} className="viewbar-item">
          <button type="button" className="viewbar-btn" title={`${v.name} (${Math.round(v.zoom * 100)}%)`} onClick={() => go(v)}>
            {v.name}
          </button>
          <button
            type="button"
            className="viewbar-more"
            aria-label={`Options for ${v.name}`}
            onClick={() => setMenuFor(menuFor === id ? null : id)}
          >
            <IconDots />
          </button>
          {menuFor === id && (
            <div className="menu viewbar-menu" ref={menuRef} role="menu">
              <button type="button" role="menuitem" onClick={act(() => board.updateView(id, currentView()))}>
                <span>Update to current view</span>
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={act(() => {
                  const name = prompt('View name', v.name);
                  if (name?.trim()) board.updateView(id, { name: name.trim() });
                })}
              >
                <span>Rename</span>
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={act(() => {
                  const base = board.remote ? paths.board(board.boardId) : paths.local(board.boardId);
                  const url = `${location.origin}${location.pathname}${base}?view=${id}`;
                  void navigator.clipboard?.writeText(url).catch(() => prompt('Link', url));
                })}
              >
                <span>Copy link</span>
              </button>
              <div className="sep" />
              <button type="button" role="menuitem" className="danger" onClick={act(() => board.deleteView(id))}>
                <span>Delete view</span>
              </button>
            </div>
          )}
        </span>
      ))}
      <button type="button" className="viewbar-btn viewbar-add" title="Save the current view" onClick={save}>
        <IconPin /> {entries.length ? '' : 'Save view'}
      </button>
    </div>
  );
}

const round = (v: number) => Math.round(v * 100) / 100;

/** Apply a view by id if it exists; returns whether it did. */
export function applyView(board: { views: { get(id: string): unknown } }, pane: ReturnType<typeof usePaneStore>, id: string): boolean {
  const y = board.views.get(id);
  if (!y || typeof y !== 'object') return false;
  const v = (y as { toJSON(): SavedView }).toJSON();
  const { size, setCamera } = pane.getState();
  setCamera(centerOn({ x: v.cx, y: v.cy }, size, v.zoom));
  return true;
}

// keep the hook import used for typing in applyView's signature
void usePane;
